import React, {Dispatch, SetStateAction, useContext, useEffect, useMemo, useState} from "react";
import {uniqueId} from "lodash";
import {useHistoryState} from "~/apps/core/utils";

type StackItem = {
    id: string
    priority: number
};

const context = React.createContext<[StackItem[], (value: StackItem[]) => Promise<void>, () => Promise<void>]>([[{}] as StackItem[], () => Promise.resolve(), () => Promise.resolve()]);

export const DialogStackProvider = ({children}) => {
    const state = useHistoryState<StackItem[]>({
        name: 'dialogStack',
        serialize: value => value && value.length ? JSON.stringify(value) : '',
        deserialize: data => {
            const raw = data && data != 'undefined' ? JSON.parse(data) : '';
            const value: StackItem[] = [] as StackItem[];
            if (raw) {
                raw.forEach((item: StackItem) => {
                    if (typeof item == "object" && typeof item.id == "string" && typeof item.priority == "number") {
                        value.push(item);
                    }
                })
            }
            return value;
        }
    })

    return (
        <>
            <context.Provider value={state}>
                {children}
            </context.Provider>
        </>
    )
}


type DialogState = "queue" | "visible" | false;

type DialogStackReturnType = {
    setOpen: (value: boolean) => Promise<void>,
    state: DialogState,
}

export function useDialogStack(onClose?: () => void, priority = 0): DialogStackReturnType {
    const id = useMemo(() => uniqueId("dialog-"), []);
    const [stack, setStack, deleteStack] = useContext(context);
    const isMounted = React.useRef(false);

    const state: DialogState = useMemo(() => {
        const index = stack?.findIndex(x => x.id == id);
        if (index == -1) {
            return false
        } else if (index == stack?.length - 1) {
            return "visible"
        } else {
            return "queue";
        }
    }, [stack, id]);

    useEffect(() => () => {
        //const stack = [];
        if (stack) {
            const currentIndex = stack.findIndex(x => x.id == id);
            // remove from stack
            if (!open && currentIndex != -1) {
                stack.splice(currentIndex, 1);
            }
            if (stack.length > 0) setStack(stack);
        }
    },[]);

    useEffect(() => {
        if (isMounted.current) {
            if (!stack) {
                deleteStack().then(() => setOpen(false))
            }
        } else {
            isMounted.current = true;
        }
    }, [stack])

    const setOpen = async (value: boolean) => {
        if (value && (state === 'queue')) {
            const insertIndex = stack?.findIndex(x => x.priority >= priority) ?? 0
            if (stack) {
                stack.splice(insertIndex, 0, {
                    id: id,
                    priority: priority
                });
                await setStack(stack);
            } else {
                await setStack([{id: id, priority: priority}]);
            }
        } else {
            const currentIndex = stack?.findIndex(x => x.id == id);
            if (currentIndex != undefined && currentIndex != -1) {
                stack?.splice(currentIndex, 1);
                if (stack?.length) {
                    await setStack(stack);
                } else {
                    await deleteStack();
                }
            }
            if (onClose) {
                onClose()
            }
        }
    }

    return {
        state,
        setOpen
    };
}

